import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import "./studentProgressJourney.css";
import { formatDate } from "../format.js";
import { getStaffRepertoire, type RepertoireEntry, type RepertoireList } from "../repertoire.js";
import {
  addGoalPracticeTask,
  createStudentGoal,
  getStaffStudentGoals,
  updateStudentGoal,
  type StudentGoal,
  type StudentGoalStatus,
  type StaffStudentGoals,
} from "../studentGoals.js";
import { useAsync, type Async } from "../useAsync.js";
import { Drawer } from "../ui/Dialog.js";

interface GoalDraft {
  title: string;
  description: string;
  targetDate: string;
  repertoireId: string;
  familyVisible: boolean;
}

const EMPTY_DRAFT: GoalDraft = {
  title: "",
  description: "",
  targetDate: "",
  repertoireId: "",
  familyVisible: false,
};

function goalDraft(goal: StudentGoal): GoalDraft {
  return {
    title: goal.title,
    description: goal.description ?? "",
    targetDate: goal.targetDate ?? "",
    repertoireId: goal.repertoireId ?? "",
    familyVisible: goal.familyVisible,
  };
}

function targetLabel(targetDate: string | null): string | null {
  return targetDate ? formatDate(`${targetDate}T12:00:00Z`) : null;
}

function statusLabel(status: StudentGoalStatus): string {
  if (status === "achieved") return "Achieved";
  if (status === "paused") return "Paused";
  if (status === "abandoned") return "Stopped";
  return "Active";
}

function repertoireName(entry: Pick<RepertoireEntry, "title" | "composer">): string {
  return entry.composer ? `${entry.composer} — ${entry.title}` : entry.title;
}

function goalRepertoireName(goal: StudentGoal): string | null {
  if (!goal.repertoireTitle) return null;
  return goal.repertoireComposer
    ? `${goal.repertoireComposer} — ${goal.repertoireTitle}`
    : goal.repertoireTitle;
}

export function StudentGoals({
  studentId,
  onChanged,
  focusGoalId,
  createInitially = true,
  creationOnly = false,
  source,
  repertoireSource,
  onCreated,
  onCancel,
}: {
  studentId: string;
  onChanged?: () => void;
  focusGoalId?: string;
  createInitially?: boolean;
  creationOnly?: boolean;
  source?: Async<StaffStudentGoals>;
  repertoireSource?: Async<RepertoireList>;
  onCreated?: () => void;
  onCancel?: () => void;
}) {
  const [historyPage, setHistoryPage] = useState(1);
  const loadedGoals = useAsync(() => creationOnly || (source && historyPage === 1)
    ? Promise.resolve(null) : getStaffStudentGoals(studentId, historyPage), [studentId, historyPage]);
  const goals = source && historyPage === 1 ? source : loadedGoals;
  const loadedRepertoire = useAsync(() => repertoireSource ? Promise.resolve(null) : getStaffRepertoire(studentId), [studentId]);
  const repertoire = repertoireSource ?? loadedRepertoire;
  const [editing, setEditing] = useState<StudentGoal | null>(null);
  const [draft, setDraft] = useState<GoalDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [practiceFor, setPracticeFor] = useState<StudentGoal | null>(null);
  const [practiceTitle, setPracticeTitle] = useState("");
  const [practiceDescription, setPracticeDescription] = useState("");
  const [achieving, setAchieving] = useState<StudentGoal | null>(null);
  const [completionNote, setCompletionNote] = useState("");
  const [editorOpen, setEditorOpen] = useState(createInitially);

  useEffect(() => {
    setHistoryPage(1);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setSaving(false);
    setProblem(null);
    setPracticeFor(null);
    setPracticeTitle("");
    setPracticeDescription("");
    setAchieving(null);
    setCompletionNote("");
    setEditorOpen(createInitially);
  }, [studentId]);

  const repertoireOptions = useMemo(() => {
    const data = repertoire.data;
    if (!data) return [];
    const rows = [...data.current, ...data.completed.items];
    const seen = new Set<string>();
    return rows.filter((entry) => {
      if (seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
  }, [repertoire.data]);

  function resetEditor() {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setEditorOpen(false);
  }

  function closePractice() {
    setPracticeFor(null);
    setPracticeTitle("");
    setPracticeDescription("");
  }

  function changed() {
    goals.reload();
    if (source && goals !== source) source.reload();
    onChanged?.();
  }

  async function saveGoal(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const input = {
        title: draft.title,
        description: draft.description || null,
        targetDate: draft.targetDate || null,
        repertoireId: draft.repertoireId || null,
        familyVisible: draft.familyVisible,
      };
      if (editing) await updateStudentGoal(studentId, editing.id, input);
      else await createStudentGoal(studentId, input);
      resetEditor();
      changed();
      if (!editing) onCreated?.();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not save that goal.");
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(goal: StudentGoal, status: StudentGoalStatus) {
    if (saving) return;
    setSaving(true);
    setProblem(null);
    try {
      await updateStudentGoal(studentId, goal.id, { status });
      if (status === "achieved" || status === "abandoned") setHistoryPage(1);
      changed();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not update that goal.");
    } finally {
      setSaving(false);
    }
  }

  async function markAchieved(event: FormEvent) {
    event.preventDefault();
    if (!achieving || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      await updateStudentGoal(studentId, achieving.id, {
        status: "achieved",
        completionNote: completionNote || null,
      });
      setAchieving(null);
      setCompletionNote("");
      setHistoryPage(1);
      changed();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not mark that goal achieved.");
    } finally {
      setSaving(false);
    }
  }

  async function addPractice(event: FormEvent) {
    event.preventDefault();
    if (!practiceFor || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      await addGoalPracticeTask(studentId, practiceFor.id, {
        title: practiceTitle,
        description: practiceDescription || null,
      });
      closePractice();
      changed();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that practice task.");
    } finally {
      setSaving(false);
    }
  }

  const data = goals.data;
  const currentGoals = data?.current.filter((goal) => !focusGoalId || goal.id === focusGoalId) ?? [];
  const focusedHistory = data?.history.some((goal) => goal.id === focusGoalId);

  return (
    <section className="stack" style={{ gap: 18 }} aria-labelledby={focusGoalId || creationOnly ? undefined : "student-goals-heading"} aria-label={creationOnly ? "Create goal" : focusGoalId ? "Goal details and actions" : undefined}>
      {!focusGoalId && !creationOnly && <div className="student-record-section-head">
        <div>
          <h2 id="student-goals-heading" style={{ marginBottom: 4 }}>Goals & milestones</h2>
          <p className="small muted" style={{ margin: 0 }}>
            Longer-term teaching direction. Progress is descriptive; goals never need a percentage.
          </p>
        </div>
        {!editorOpen && <button type="button" onClick={() => setEditorOpen(true)}>Add goal</button>}
      </div>}

      {problem && <div className="error">{problem}</div>}
      {!creationOnly && goals.error && <div className="error">We could not load this student&rsquo;s goals.</div>}

      {editorOpen && <form className="panel stack" style={{ gap: 12 }} onSubmit={saveGoal}>
        <div className="spread">
          <strong>{editing ? "Update goal" : "New goal"}</strong>
          <button type="button" className="quiet small" onClick={creationOnly ? onCancel ?? resetEditor : resetEditor}>
            {editing ? "Cancel edit" : "Cancel"}
          </button>
        </div>
        <label>
          <span className="small muted">Goal</span>
          <input
            required
            maxLength={200}
            value={draft.title}
            onChange={(event) => setDraft((value) => ({ ...value, title: event.target.value }))}
            placeholder="Prepare Grade 5 scales"
          />
        </label>
        <label>
          <span className="small muted">Current focus or description</span>
          <textarea
            rows={3}
            maxLength={2000}
            value={draft.description}
            onChange={(event) => setDraft((value) => ({ ...value, description: event.target.value }))}
          />
        </label>
        <div className="row" style={{ alignItems: "end", flexWrap: "wrap" }}>
          <label>
            <span className="small muted">Target date (optional)</span>
            <input
              type="date"
              value={draft.targetDate}
              onChange={(event) => setDraft((value) => ({ ...value, targetDate: event.target.value }))}
            />
          </label>
          <label style={{ minWidth: 240 }}>
            <span className="small muted">Repertoire (optional)</span>
            <select
              value={draft.repertoireId}
              onChange={(event) => setDraft((value) => ({ ...value, repertoireId: event.target.value }))}
            >
              <option value="">No linked piece</option>
              {repertoireOptions.map((entry) => (
                <option key={entry.id} value={entry.id}>{repertoireName(entry)}</option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={draft.familyVisible}
              onChange={(event) => setDraft((value) => ({ ...value, familyVisible: event.target.checked }))}
            />
            <span>Show this wording to the student/family</span>
          </label>
          <button type="submit" disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create goal"}
          </button>
        </div>
        {repertoire.error && (
          <p className="small muted" role="status">
            Repertoire could not be loaded; the goal can still be saved without a piece link.
          </p>
        )}
      </form>}

      {!creationOnly && (!focusGoalId || currentGoals.length > 0) && <section aria-labelledby={focusGoalId ? undefined : "current-goals-heading"} aria-label={focusGoalId ? "Goal details" : undefined}>
        {!focusGoalId && <h3 id="current-goals-heading">Current goals</h3>}
        {goals.loading && !data && <p className="empty">Loading goals…</p>}
        {data && currentGoals.length === 0 && (
          <div className="panel"><p className="empty">No active goals yet.</p></div>
        )}
        <div className="stack" style={{ gap: 12 }}>
          {currentGoals.map((goal) => (
            <article className="panel" key={goal.id}>
              <div className="spread" style={{ alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <strong>{goal.title}</strong>
                    <span className={`tag${goal.status === "paused" ? " warn" : ""}`}>
                      {statusLabel(goal.status)}
                    </span>
                    {goal.familyVisible && <span className="tag">Family visible</span>}
                  </div>
                  {goalRepertoireName(goal) && (
                    <div className="small muted" style={{ marginTop: 4 }}>{goalRepertoireName(goal)}</div>
                  )}
                  {goal.description && <p className="notes">{goal.description}</p>}
                  {goal.createdByName && <div className="small muted progress-meta">Added by {goal.createdByName} · {formatDate(goal.createdAt)}</div>}
                  <div className="small muted">
                    {targetLabel(goal.targetDate) ? `Target ${targetLabel(goal.targetDate)}` : "No target date"}
                    {goal.linkedPracticeTasks > 0 &&
                      ` · ${goal.linkedPracticeTasks} linked practice ${goal.linkedPracticeTasks === 1 ? "task" : "tasks"}`}
                  </div>
                </div>
              </div>
              <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="quiet small"
                  onClick={() => {
                    setEditing(goal);
                    setDraft(goalDraft(goal));
                    setEditorOpen(true);
                  }}
                >
                  Update
                </button>
                {goal.status === "active" && (
                  <button
                    type="button"
                    className="quiet small"
                    onClick={() => {
                      setPracticeFor(goal);
                      setPracticeTitle(goal.title);
                      setPracticeDescription("");
                    }}
                  >
                    Add practice task
                  </button>
                )}
                <button
                  type="button"
                  className="quiet small"
                  disabled={saving}
                  onClick={() => void changeStatus(goal, goal.status === "paused" ? "active" : "paused")}
                >
                  {goal.status === "paused" ? "Resume" : "Pause"}
                </button>
                <button
                  type="button"
                  className="quiet small"
                  onClick={() => {
                    setAchieving(goal);
                    setCompletionNote("");
                  }}
                >
                  Mark achieved
                </button>
                <button
                  type="button"
                  className="quiet small"
                  disabled={saving}
                  onClick={() => void changeStatus(goal, "abandoned")}
                >
                  Stop goal
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>}
      {!creationOnly && focusGoalId && currentGoals.length === 0 && !focusedHistory && <p className="empty">
        {goals.loading ? "Loading goal details…" : goals.error ? "Goal details could not be loaded." : "This goal is no longer in the current list or this history page."}
      </p>}

      {practiceFor && (
        <form className="panel stack" style={{ gap: 10 }} onSubmit={addPractice}>
          <div className="spread">
            <strong>Add practice for “{practiceFor.title}”</strong>
            <button type="button" className="quiet small" onClick={closePractice}>Cancel</button>
          </div>
          <label>
            <span className="small muted">Practice task</span>
            <input required maxLength={200} value={practiceTitle} onChange={(event) => setPracticeTitle(event.target.value)} />
          </label>
          <label>
            <span className="small muted">Instructions (optional)</span>
            <textarea rows={2} maxLength={2000} value={practiceDescription} onChange={(event) => setPracticeDescription(event.target.value)} />
          </label>
          <div><button type="submit" disabled={saving}>{saving ? "Adding…" : "Add to Practice"}</button></div>
        </form>
      )}

      {achieving && (
        <form className="panel stack" style={{ gap: 10 }} onSubmit={markAchieved}>
          <div className="spread">
            <strong>Mark “{achieving.title}” achieved</strong>
            <button
              type="button"
              className="quiet small"
              onClick={() => {
                setAchieving(null);
                setCompletionNote("");
              }}
            >
              Cancel
            </button>
          </div>
          <label>
            <span className="small muted">Completion note (staff only, optional)</span>
            <textarea rows={2} maxLength={2000} value={completionNote} onChange={(event) => setCompletionNote(event.target.value)} />
          </label>
          <div><button type="submit" disabled={saving}>{saving ? "Saving…" : "Mark achieved"}</button></div>
        </form>
      )}

      {!creationOnly && (!focusGoalId || focusedHistory) && <section aria-labelledby="goal-history-heading">
        <h3 id="goal-history-heading">Milestone history</h3>
        {data && data.history.length === 0 && (
          <div className="panel"><p className="empty">No achieved or stopped goals yet.</p></div>
        )}
        <div className="stack" style={{ gap: 10 }}>
          {data?.history.filter((goal) => !focusGoalId || goal.id === focusGoalId).map((goal) => (
            <article className="panel" key={goal.id}>
              <div className="spread" style={{ gap: 12, alignItems: "flex-start" }}>
                <div>
                  <strong>{goal.title}</strong>
                  {goalRepertoireName(goal) && (
                    <div className="small muted progress-meta-small">{goalRepertoireName(goal)}</div>
                  )}
                  {goal.description && <div className="small muted progress-meta-small">{goal.description}</div>}
                  {goal.createdByName && <div className="small muted progress-meta-small">Added by {goal.createdByName} · {formatDate(goal.createdAt)}</div>}
                  <div className="small muted progress-meta-small">
                    {goal.status === "achieved" && goal.achievedAt
                      ? `Achieved ${formatDate(goal.achievedAt)}`
                      : `Stopped ${formatDate(goal.updatedAt)}`}
                    {goal.completionNote && ` · ${goal.completionNote}`}
                  </div>
                </div>
                <span className="tag">{statusLabel(goal.status)}</span>
              </div>
            </article>
          ))}
        </div>
        {data && !focusGoalId && data.historyPages > 1 && (
          <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
            <button type="button" className="quiet small" disabled={historyPage <= 1} onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}>
              Previous
            </button>
            <span className="small muted">Page {data.historyPage} of {data.historyPages}</span>
            <button type="button" className="quiet small" disabled={historyPage >= data.historyPages} onClick={() => setHistoryPage((page) => page + 1)}>
              Next
            </button>
          </div>
        )}
      </section>}
    </section>
  );
}

export function StudentGoalsSnapshot({ studentId, createRequest, goals, repertoire, onChanged }: {
  studentId: string;
  createRequest: number;
  onChanged: () => void;
  goals: Async<StaffStudentGoals>;
  repertoire: Async<RepertoireList>;
}) {
  const headingId = useId();
  const [manager, setManager] = useState<{ kind: "all" | "new" | "goal"; goalId?: string } | null>(null);
  useEffect(() => {
    if (createRequest > 0) setManager({ kind: "new" });
  }, [createRequest]);
  const data = goals.error ? null : goals.data;
  const current = data?.current ?? [];
  const visible = [...current.filter((goal) => goal.status === "active"), ...current.filter((goal) => goal.status !== "active")].slice(0, 3);
  return <section className="panel student-progress-summary" aria-labelledby={headingId}>
    <div className="student-progress-summary-head">
      <h3 id={headingId}>Current goals{data && <span className="muted"> · {current.length}</span>}</h3>
      <button type="button" className="student-progress-detail-link" onClick={() => setManager({ kind: "all" })}>All goals</button>
    </div>
    {goals.loading && !data && <p className="small muted" role="status">Loading goals…</p>}
    {goals.error && <p className="small" role="alert">Goals could not be loaded. <button type="button" className="student-progress-detail-link" onClick={goals.reload}>Retry</button></p>}
    {data && current.length === 0 && <p className="small muted">No current goals yet.</p>}
    {visible.map((goal) => <article className="student-progress-summary-row" key={goal.id}>
      <div className="student-progress-summary-title"><strong>{goal.title}</strong><span className={`tag${goal.status === "active" ? " warn" : ""}`}>{statusLabel(goal.status)}</span></div>
      {goal.description && <p className="small muted student-progress-excerpt">{goal.description}</p>}
      {goal.createdByName && <p className="small muted">Added by {goal.createdByName} · {formatDate(goal.createdAt)}</p>}
      <div className="student-progress-summary-foot">
        <span className="small muted">{targetLabel(goal.targetDate) ? `Target ${targetLabel(goal.targetDate)}` : "No target date"}{goal.linkedPracticeTasks > 0 && ` · ${goal.linkedPracticeTasks} linked ${goal.linkedPracticeTasks === 1 ? "task" : "tasks"}`}</span>
        <button type="button" className="student-progress-detail-link" aria-label={`Details for goal ${goal.title}`} onClick={() => setManager({ kind: "goal", goalId: goal.id })}>Details</button>
      </div>
    </article>)}
    {current.length > visible.length && <p className="small muted">Showing {visible.length} of {current.length} current goals.</p>}
    {manager && <Drawer title={manager.kind === "new" ? "New goal" : manager.kind === "all" ? "Goals & milestones" : current.find((goal) => goal.id === manager.goalId)?.title ?? "Goal details"} onClose={() => setManager(null)} size="large" className="student-progress-drawer">
      <StudentGoals key={`${manager.kind}:${manager.goalId ?? ""}`} studentId={studentId} focusGoalId={manager.goalId}
        createInitially={manager.kind === "new"} creationOnly={manager.kind === "new"} source={goals} repertoireSource={repertoire}
        onChanged={onChanged} onCreated={manager.kind === "new" ? () => setManager(null) : undefined} onCancel={() => setManager(null)} />
    </Drawer>}
  </section>;
}
